import React from "react";
import { safeHref } from "../utils/safeUrl";
import "../CustomTheme/customTheme.css";
import "./CustomIconButton.css";

const TONES = ["primary", "success", "danger", "warning", "info", "neutral", "whatsapp"];

/**
 * CustomIconButton — a button that shows ANY icon you pass.
 *
 *   // round icon button (tables, toolbars)
 *   <CustomIconButton icon={<WhatsAppLogo />} tone="whatsapp" title="Chat on WhatsApp" onClick={chat} />
 *   <CustomIconButton icon={<Phone size={16} />} tone="success" title="Call" href={`tel:${mobile}`} />
 *   <CustomIconButton icon={<Download size={16} />} title="Download" onClick={download} />
 *
 *   // full button with text
 *   <CustomIconButton variant="full" icon={<WhatsAppLogo />} tone="whatsapp" onClick={chat}>
 *     Chat on WhatsApp
 *   </CustomIconButton>
 *
 *   // any brand colour
 *   <CustomIconButton icon={<Mail size={16} />} color="#ea4335" title="Email" />
 *
 * Props:
 *  - icon        the icon element to show (lucide, MUI, or an SVG)
 *  - children | label   text, only shown for variant="full"
 *  - variant     "icon" (default: round, soft tint → solid on hover) | "full" (solid with text)
 *  - tone        "primary" (default) | "success" | "danger" | "warning" | "info" | "neutral" | "whatsapp"
 *  - color       any CSS colour; overrides tone (soft tint is derived from it)
 *  - size        "sm" | "md" (default) | "lg"
 *  - title       tooltip + screen-reader label (required for variant="icon")
 *  - href, target   render as a link (e.g. "tel:…", "mailto:…", a WhatsApp URL)
 *  - onClick, disabled, type, className, style
 * Clicks never bubble, so it's safe inside clickable table rows.
 */
const CustomIconButton = ({
  icon,
  children,
  label,
  variant = "icon",
  tone = "primary",
  color,
  size = "md",
  title,
  href,
  target,
  onClick,
  disabled = false,
  type = "button",
  className,
  style,
}) => {
  const text = children ?? label;
  const toneClass = TONES.includes(tone) ? tone : "primary";
  // prefixed classes so a prop value can never clash with an element class
  const classes = `cib-btn cib-variant-${variant === "full" ? "full" : "icon"} cib-size-${size} cib-tone-${toneClass}${
    color ? " cib-custom" : ""
  }${disabled ? " cib-disabled" : ""}${className ? ` ${className}` : ""}`;
  const css = color ? { "--cib-color": color, ...style } : style;

  const handleClick = (e) => {
    e.stopPropagation(); // never trigger a table row's onRowClick
    if (disabled) {
      e.preventDefault();
      return;
    }
    onClick?.(e);
  };

  const content = (
    <>
      {icon && (
        <span className="cib-icon" aria-hidden="true">
          {icon}
        </span>
      )}
      {variant === "full" && text && <span className="cib-label">{text}</span>}
    </>
  );

  const shared = {
    className: classes,
    style: css,
    title,
    onClick: handleClick,
    "aria-label": variant === "icon" ? title : undefined,
  };

  if (href) {
    return (
      <a
        {...shared}
        href={disabled ? undefined : safeHref(href)}
        target={target}
        rel={target === "_blank" ? "noopener noreferrer" : undefined}
        aria-disabled={disabled || undefined}
      >
        {content}
      </a>
    );
  }

  return (
    <button {...shared} type={type} disabled={disabled}>
      {content}
    </button>
  );
};

export default CustomIconButton;
